import { Injectable } from '@angular/core';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import { CreateExportJobDTO, ExportJobClient, ExportJobDTO, ExportJobExecutionLogDTO, ExportJobType, FileResponse, FilterDTO, PagedResultDTOOfExportJobLimitedDTO, SortDirection } from 'src/app/base/models/models.swagger.generated';
import { ToastService } from 'src/app/base/services/toast.service';
import { ShowInactiveService } from 'src/app/base/shared/grid-base/show-inactive.service';
import { IAdaptorService } from 'src/app/base/shared/syncfusion/adaptor-service.interface';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { ErrorType } from 'src/app/core/routing/error-types';

@Injectable({
  providedIn: 'root'
})

export class ExportJobService extends ShowInactiveService implements IAdaptorService {


  public selectedClientId: number;

  constructor(
    private exportJobClient: ExportJobClient,
    private toastService: ToastService,
    private spinnerService: SpinnerService
  ) {
    super();
  }

  getData(skip: number, take: number, filters: FilterDTO[], sortBy: string, sortDirection: SortDirection): Observable<PagedResultDTOOfExportJobLimitedDTO> {
    this.spinnerService.showSpinner = true;
    if (!filters) {
      filters = [];
    }
    return this.exportJobClient.searchExportJobs(this.selectedClientId, filters, skip, take, sortBy, sortDirection).pipe(
      tap(() => {
        this.spinnerService.showSpinner = false;
      }),
      catchError((error: any) => {
        this.spinnerService.showSpinner = false;
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        }
        return throwError(() => error);
      })
    );
  }

  addRecord(data: CreateExportJobDTO): Observable<ExportJobDTO> {
    this.spinnerService.showSpinner = true;
    return this.exportJobClient.createExportJob(data).pipe(
      tap(() => {
        this.spinnerService.showSpinner = false;
        this.toastService.showSuccess('i18n.domain.export-jobs.messages.add-title', 'i18n.domain.export-jobs.messages.add-message');
      }),
      catchError((error: any) => {
        this.spinnerService.showSpinner = false;
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showApiExeption(error, 'i18n.errors.server-error.title', 'i18n.errors.server-error.message');
        }
        return throwError(() => error);
      })
    );
  }

  updateRecord(data: any): Observable<any> {
    throw new Error('Method not implemented.');
  }

  deleteRecord(id: number): Observable<any> {
    this.spinnerService.showSpinner = true;
    return this.exportJobClient.deleteExportJob(id).pipe(
      tap(() => {
        this.spinnerService.showSpinner = false;
        this.toastService.showSuccess('i18n.domain.export-jobs.messages.delete-title', 'i18n.domain.export-jobs.messages.delete-message');
      }),
      catchError((error: any) => {
        this.spinnerService.showSpinner = false;
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        }
        return throwError(() => error);
      })
    );
  }

  getById(id: number): Observable<ExportJobDTO> {
    this.spinnerService.showSpinner = true;
    return this.exportJobClient.getExportJob(id).pipe(
      tap(() => {
        this.spinnerService.showSpinner = false;
      }),
      catchError((error: any) => {
        this.spinnerService.showSpinner = false;
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        }
        return throwError(() => error);
      })
    );
  }

  downloadOutputFile(id: number): Observable<Blob> {
    this.spinnerService.showSpinner = true;
    return this.exportJobClient.downloadOutputFile(id).pipe(
      tap(() => {
        this.spinnerService.showSpinner = false;
      }),
      map((response: any) => {
        if (response && response.data instanceof Blob) {
          return response.data;
        }
        throw new Error('Invalid file response');
      }),
      catchError((error: any) => {
        this.spinnerService.showSpinner = false;
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        }
        return throwError(() => error);
      })
    );
  }

  public downloadTemplateFile(type: ExportJobType): Observable<Blob> {
    this.spinnerService.showSpinner = true;
    return this.exportJobClient.getExportJobTemplate(type).pipe(
      tap(() => {
        this.spinnerService.showSpinner = false;
      }),
      map((response: FileResponse) => {
        if (response && response.data instanceof Blob) {
          return response.data;
        }
        throw new Error('Invalid file response');
      }),
      catchError((error: any) => {
        this.spinnerService.showSpinner = false;
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        }
        return throwError(() => error);
      })
    );
  }

  public getExecutionLog(jobId: number): Observable<ExportJobExecutionLogDTO> {
    this.spinnerService.showSpinner = true;
    return this.exportJobClient.getExecutionLog(jobId).pipe(
      tap(() => {
        this.spinnerService.showSpinner = false;
      }),
      catchError((error: any) => {
        this.spinnerService.showSpinner = false;
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        }
        return throwError(() => error);
      })
    );
  }
}

